import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { EvaluationService } from '@buf/alconostmt_services.bufbuild_es/alconost/mt/evaluation_pb.js';

// Create a transport
const transport = createConnectTransport({
    baseUrl: 'https://services-375925469890.us-central1.run.app',
});

// Create a client
const evaluationClient = createClient(EvaluationService, transport);

// Create a new evaluation
export const createEvaluation = async ({
    id,
    name,
    sourceLanguage,
    targetLanguage,
    promptId,
}) => {
    try {
        const request = {
            id,
            name,
            sourceLanguage,
            targetLanguage,
            promptId,
        };

        const response = await evaluationClient.createEvaluation(request);
        console.log('Create evaluation response:', response);
        return response.evaluation;
    } catch (error) {
        console.error('Error creating evaluation:', error);
        throw error;
    }
};

// Get an evaluation by ID
export const getEvaluation = async (id) => {
    try {
        const response = await evaluationClient.getEvaluation({ id });
        console.log('Get evaluation response:', response);
        return response.evaluation;
    } catch (error) {
        console.error('Error getting evaluation:', error);
        return null;
    }
};

// List all evaluations
export const listEvaluations = async ({
    pageSize = 10,
    pageNumber = 1,
    status,
    searchTerm,
    sourceLanguage,
    targetLanguage
} = {}) => {
    try {
        const request = {
            pageSize,
            pageNumber,
            status,
            searchTerm,
            sourceLanguage,
            targetLanguage
        };

        // Remove undefined fields
        Object.keys(request).forEach(key =>
            request[key] === undefined && delete request[key]
        );

        const response = await evaluationClient.listEvaluations(request);

        return {
            evaluations: response.evaluations,
            totalCount: response.totalCount,
            pageCount: response.pageCount
        };
    } catch (error) {
        console.error('Error listing evaluations:', error);
        throw error;
    }
};

// Evaluate segments
export const evaluate = async (id, modelId) => {
    try {
        const response = await evaluationClient.evaluate({
            evaluationId: id,
            engine: modelId,
        });
        console.log('Evaluate response:', response);
        return response;
    } catch (error) {
        console.error('Error evaluating segments:', error);
        throw error;
    }
};

// List available models
export const listModels = async () => {
    try {
        const response = await evaluationClient.listModels({});
        return response.models;
    } catch (error) {
        console.error('Error listing models:', error);
        throw error;
    }
};

// Prompt - returns API response object (includes evaluationPrompt)
export const getPrompt = async (model) => {
    try {
        const response = await evaluationClient.getPrompt({ model });
        return response ?? {};
    } catch (error) {
        console.error('Error getting prompt:', error);
        throw error;
    }
};

// Estimate evaluation
export const estimateEvaluation = async (id, modelId) => {
    try {
        const response = await evaluationClient.estimate({
            evaluationId: id,
            model: modelId
        });

        return response.estimate;
    } catch (error) {
        console.error('Error estimating evaluation:', error);
        throw error;
    }
};

// Update evaluation
export const updateEvaluation = async (evaluationId, field, value) => {
    try {
        const request = {
            evaluationId: evaluationId,
            field: {
                value: value,
                case: field
            }
        };

        const response = await evaluationClient.updateEvaluation(request);
        return response;
    } catch (error) {
        console.error('Error updating evaluation:', error);
        throw error;
    }
};

// Duplicate evaluation
export const duplicateEvaluation = async (sourceId, targetId, model, name) => {
    try {
        const response = await evaluationClient.duplicateEvaluation({
            sourceId: sourceId,
            targetId: targetId,
            model: model,
            name: name
        });

        return response;
    } catch (error) {
        console.error('Failed to duplicate evaluation:', error);
        throw error;
    }
};

// Get limits for chars
export const getConfig = async () => {
    try {
        const response = await evaluationClient.getConfig({});
        return response;
    } catch (error) {
        console.error('Error getting config:', error);
        throw error;
    }
};

// Detect language
export const detectLanguage = async (text) => {
    try {
        const response = await evaluationClient.detectLanguage({
            text: text
        });
        return response.language;
    } catch (error) {
        console.error('Error detecting language:', error);
        throw error;
    }
};